<template>
  <div class="flex flex-col w-full justify-center">
    <div v-if="!isEditing" class="flex items-center gap-2">
      <div>
        <div class="flex items-center">
          <h1 class="font-bold text-left text-black text-xl">
            {{ modelValue || 'Sem nome' }}
          </h1>
          <v-icon
            v-if="editable"
            :icon="mdiPencilOutline"
            class="cursor-pointer text-primary40 ml-2 hover:opacity-70 transition-opacity"
            size="20"
            data-testid="editable-header-edit-button"
            @click="startEditing"
          />
        </div>
        <p v-if="subtitle" class="text-start text-[#323232] text-md mt-1 font-normal">
          {{ subtitle }}
        </p>
        <p v-if="errorMessages" class="text-red-500 text-xs mt-1 text-left">
          {{ errorMessages }}
        </p>
      </div>
    </div>

    <div v-else class="flex w-full md:w-2/4 items-center gap-4">
      <div class="flex-1">
        <input
          v-model="tempName"
          ref="nameInput"
          type="text"
          class="border rounded-md px-2 py-1 w-full"
          :class="errorMessages ? 'border-red-500' : 'border-gray-300'"
          :placeholder="placeholder"
          @keydown.enter.prevent="saveEdit"
          @keydown.esc.prevent="cancelEdit"
          @blur="handleBlur"
          @input="handleInput"
        />
        <p v-if="errorMessages" class="text-red-500 text-xs mt-1 text-left">
          {{ errorMessages }}
        </p>
      </div>
      <div class="flex gap-2">
        <v-icon
          :icon="mdiCheck"
          class="cursor-pointer text-green-600 hover:text-green-700"
          size="28"
          @mousedown.prevent="saveEdit"
        />
        <v-icon
          :icon="mdiClose"
          class="cursor-pointer text-red-600 hover:text-red-700"
          size="28"
          @mousedown.prevent="cancelEdit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue'
import { mdiPencilOutline, mdiCheck, mdiClose } from '@mdi/js'
import { useToast } from 'vue-toastification'

interface Props {
  modelValue: string
  subtitle?: string
  isInitialEditing?: boolean
  placeholder?: string
  errorMessages?: string
  editable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isInitialEditing: false,
  placeholder: 'Nome da política',
  editable: true
})

/* eslint-disable sonarjs/no-duplicate-string */
const emit = defineEmits<{
  'update:modelValue': [value: string]
  save: [value: string]
  cancel: []
  'clear-error': []
}>()

const toast = useToast()
const isEditing = ref(props.isInitialEditing && props.editable)
const tempName = ref(props.modelValue)
const nameInput = ref<HTMLInputElement | null>(null)

onMounted(() => {
  if (isEditing.value) {
    nextTick(() => nameInput.value?.focus())
  }
})

watch(
  () => props.modelValue,
  (newVal) => {
    if (!isEditing.value) tempName.value = newVal
  }
)

const startEditing = async () => {
  if (!props.editable) return
  tempName.value = props.modelValue
  isEditing.value = true
  await nextTick()
  nameInput.value?.focus()
}

const handleInput = () => {
  emit('update:modelValue', tempName.value)
  emit('clear-error')
}

const saveEdit = () => {
  const trimmedValue = tempName.value.trim()
  if (!trimmedValue) {
    toast.error('Nome inválido.')
    nameInput.value?.focus()
    return
  }
  emit('update:modelValue', trimmedValue)
  emit('save', trimmedValue)
  isEditing.value = false
}

const handleBlur = () => {
  const trimmedValue = tempName.value.trim()
  if (!trimmedValue) {
    isEditing.value = false
    return
  }
  emit('update:modelValue', trimmedValue)
  emit('save', trimmedValue)
  isEditing.value = false
}

const cancelEdit = () => {
  tempName.value = props.modelValue
  isEditing.value = false
  emit('cancel')
}
</script>
